/**
 * MVP-274 R1. The code keypad's whole decision surface, kept out of the component so it can be
 * tested as arithmetic instead of as a DOM.
 *
 * WHY A KEYPAD AT ALL
 *
 * The guest page is reached by scanning a QR code on a table, in a dark room, usually by somebody
 * older than the people who built it, holding the venue's printed song book. That book is ordered
 * by catalogue code, every song in it has its code printed next to it, and the backend has always
 * matched on that code — `catalogId` is folded into `searchText` (song lifecycles) and an exact
 * match is the TOP ranking tier (`ranking.js`'s `buildTierCase`). Until this task the only way in
 * was a free-text field: the customer had the answer printed in their hand and no place to type it.
 *
 * The digits go into the SAME `query` the search field writes to, so there is one search effect and
 * one result list. This module only owns what a key press does to the string.
 */

/** Long enough for the catalogue as it stands ('0001'..'2238') with room to grow, short enough that
 * a stuck key cannot silently build a query nobody can read on a phone. Nothing enforces this
 * server-side; it is a keypad affordance, not a validation rule. */
export const MAX_CODE_LENGTH = 6;

/** Every key the pad can send. Digits are the literal character so the component never has to
 * translate between what is drawn on a key and what it means. */
export type CodeKey = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'back' | 'clear';

/** The ten digits, in keypad order (phone layout: 1-2-3 on the top row, 0 at the bottom). */
export const CODE_DIGITS: CodeKey[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * What the entered code becomes after one key press.
 *
 * Leading zeros are KEPT: the book prints '0551', not '551', and a customer copying what is in
 * front of them must get the song they are looking at. The exact-code ranking tier matches on the
 * stored `catalog_id` string, so '0551' and '551' are not the same query — stripping the zero would
 * silently turn a direct hit into a fuzzy one.
 */
export function applyCodeKey(current: string, key: CodeKey): string {
  if (key === 'clear') return '';
  if (key === 'back') return current.slice(0, -1);
  if (current.length >= MAX_CODE_LENGTH) return current;
  return current + key;
}

/**
 * True when a physical keyboard event should be routed into the pad rather than left to the
 * browser. A phone shows the pad, but this page is also opened on laptops (a KJ checking what the
 * room sees, a guest on a tablet with a keyboard), and a pad you cannot type into with the keyboard
 * in front of you is a worse door than the one it replaced.
 */
export function keyFromKeyboardEvent(key: string): CodeKey | null {
  if (key.length === 1 && key >= '0' && key <= '9') return key as CodeKey;
  if (key === 'Backspace') return 'back';
  if (key === 'Escape') return 'clear';
  return null;
}
